import axios from 'axios'

// De Laravel-API antwoordt in snake_case; de hele frontend (types, stores,
// components) is geschreven in camelCase. Deze transformatie is de enige
// brug daartussen — requests blijven onaangeraakt (die zijn al snake_case).
function toCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase())
}

function keysToCamel(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(keysToCamel)
  }
  if (value !== null && typeof value === 'object' && (value as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [toCamel(k), keysToCamel(v)])
    )
  }
  return value
}

// Create axios instance with default config
//
// Bewust géén vaste Content-Type: axios zet er zelf application/json op zodra
// de body een object is. Stond hij hier wél hard, dan zag axios bij een upload
// "json" staan en maakte het van de FormData alsnog JSON ({"image":{}}) — het
// bestand verdween onderweg en de API antwoordde "The image field is required".
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Accept': 'application/json',
  },
  withCredentials: true, // For Sanctum cookie authentication
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Geen hub-header meer (SAAS_PLAN.md §15, fase 3): de host bepaalt welke
    // hub dit is. Zolang de app dit meestuurde kon een opgeslagen keuze in de
    // browser over het adres heen, en toonde dezelfde URL bij twee mensen iets
    // anders.

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
//
// LET OP: alleen gelúkte antwoorden worden naar camelCase omgezet. Een
// foutbody (4xx/5xx) gaat onbewerkt door en houdt dus snake_case — bewust,
// want `errors` is daar gesleuteld op véldnamen, en die moeten letterlijk
// blijven om ze aan een formulierveld te kunnen koppelen. Lees velden uit
// een foutbody dus altijd in snake_case (zie readContentLock in stores/access).
api.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object') {
      response.data = keysToCamel(response.data)
    }
    return response
  },
  (error) => {
    // De auth-endpoints handelen hun eigen fouten af en tonen een nette
    // melding in het formulier. Een harde redirect hierop zou die melding
    // wegvagen (volledige page-load) — de gebruiker ziet dan alleen "terug
    // naar login zonder reden". Sla het 401-afhandelen voor die routes over.
    const url: string = error.config?.url || ''
    const isAuthEndpoint = /\/auth\/(login|forgot-password|reset-password|handoff)$/.test(url)

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Clear token and redirect to login
      localStorage.removeItem('auth_token')

      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    
    // Bestaat deze omgeving wel? (fase 0 uit SAAS_PLAN §15d)
    //
    // Nginx weet niet welke klanten er zijn en serveert de app voor élk
    // subdomein, dus de bezoeker heeft al een pagina met status 200 vóórdat
    // wij iets kunnen zeggen. Zonder dit blok start de app op, faalt elk
    // verzoek met 404, en blijft er een lege site over — wie één letter
    // verkeerd typt in een klantnaam denkt dat zijn documentatie weg is.
    //
    // Alleen op de code afgaan, niet op de status: een gewone 404 betekent
    // "die pagina bestaat niet" en hoort de bezoeker níét van zijn omgeving
    // af te helpen.
    if (
      error.response?.status === 404 &&
      error.response.data?.code === 'unknown_environment' &&
      window.location.pathname !== '/unknown-environment'
    ) {
      // De host meegeven zodat het scherm kan tonen wát er niet bestaat; dat
      // is precies wat iemand nodig heeft die de link doorstuurt.
      const host = encodeURIComponent(window.location.host)
      window.location.href = `/unknown-environment?host=${host}`
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      // Tenant-membership gate: een ingelogd niet-lid op deze omgeving. De
      // API blokkeert de data al (veilig), maar de SPA moet de gebruiker niet
      // op een half-geladen shell laten zitten — token wissen en terug naar
      // login. Andere 403's (lid zonder rechten voor één actie) blijven zoals
      // ze waren, zodat we die gebruiker niet onterecht uitloggen.
      if (error.response.data?.code === 'environment_forbidden') {
        localStorage.removeItem('auth_token')
        // Reden meegeven zodat het loginscherm uitlegt waaróm je terug bent
        // (anders: onverklaarbaar "terug naar login"). LoginView leest en
        // wist deze vlag bij het tonen.
        sessionStorage.setItem('auth_notice', 'environment_forbidden')
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      } else if (error.response.data?.code !== 'content_locked') {
        // content_locked is geen fout maar een verwachte toestand: de
        // afscherming staat aan en de app toont er een slotscherm voor.
        // Wél loggen zou de console van elke afgeschermde site vervuilen.
        console.error('Access denied:', error.response.data?.message)
      }
    }
    
    // Handle 402 Payment Required (alleen-lezen of opgeschorte omgeving)
    if (error.response?.status === 402) {
      // De backend (EnsureAccountIsWritable) stuurt hier een machine-leesbare
      // code mee, juist zodat de app kan uitleggen wat er aan de hand is in
      // plaats van een generieke "opslaan mislukt". Die code werd nergens
      // gelezen: een gebruiker van wie de proefperiode afliep kreeg midden in
      // het werk een onverklaarbare foutmelding, of - erger - de rauwe
      // Nederlandse servertekst in een Engelstalige interface.
      const code: string | undefined = error.response.data?.code
      error.accountReadOnly = code === 'account_read_only'
      error.accountSuspended = code === 'account_suspended'
    }

    // Handle 422 Validation errors
    if (error.response?.status === 422) {
      // Return validation errors in a consistent format
      error.validationErrors = error.response.data.errors || {}
    }
    
    // Handle 429 Too Many Requests (rate limiting)
    if (error.response?.status === 429) {
      console.warn('Rate limited. Please wait before making more requests.')
    }
    
    // Handle 500 Server errors
    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data.message || 'Internal server error')
    }
    
    return Promise.reject(error)
  }
)

/**
 * De foutmelding uit een API-antwoord, met terugval.
 *
 * De backend gebruikt twee sleutels naast elkaar: Laravel-validatie en de
 * meeste endpoints antwoorden met `message`, maar een aantal (waaronder de
 * Figma-import en -sync) met `error`. Wie alleen `message` las, kreeg altijd
 * de algemene terugval te zien - dus "importeren mislukt" in plaats van "je
 * hebt het maximum van 5 bestanden bereikt" of "koppel eerst je Figma-account".
 * Precies de zinnen die de gebruiker verder helpen.
 *
 * Eén plek, zodat een volgende sleutel niet weer over tientallen catch-blokken
 * verspreid hoeft te worden.
 */
export function apiFoutmelding(err: any, terugval: string): string {
  const data = err?.response?.data

  return data?.message || data?.error || terugval
}

export default api
